import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, Upload, X, MapPin } from 'lucide-react';

export function AddressLookup({ onSelect, placeholder = 'Start typing an address…' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = async (q) => {
    if (q.length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      // Search US and UK only for now (countrycodes=us,gb)
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&countrycodes=us,gb&limit=6&dedupe=1`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const data = await res.json();
      setResults(data);
      setOpen(data.length > 0);
    } catch {}
    setLoading(false);
  };

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(q), 400);
  };

  const handleSelect = (item) => {
    const a = item.address || {};
    // Normalise fields across US and UK Nominatim responses
    const streetNum  = a.house_number || '';
    const road       = a.road || a.street || '';
    const address    = [streetNum, road].filter(Boolean).join(' ');
    const city       = a.city || a.town || a.village || a.hamlet || a.suburb || '';
    const state      = a.state || a.county || '';
    const postcode   = a.postcode || '';
    const countryCode = a.country_code?.toUpperCase();
    const country    = countryCode === 'US' ? 'United States'
                     : countryCode === 'GB' ? 'United Kingdom'
                     : a.country || '';
    onSelect({ address, city, state_province: state, zip_postal: postcode, country });
    setQuery(item.display_name.split(',').slice(0, 2).join(', '));
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={handleChange}
          placeholder={placeholder}
          className="pl-9"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin rounded-full border border-muted border-t-primary" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border rounded-xl shadow-xl overflow-hidden max-h-56 overflow-y-auto">
          {results.map((item, i) => (
            <button key={i} className="w-full flex items-start gap-2 px-3 py-2.5 hover:bg-accent text-left text-sm"
              onClick={() => handleSelect(item)}>
              <MapPin className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
              <span className="truncate text-xs">{item.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
export function PostcodeLookup({ onFound }) {
  const [postcode, setPostcode] = useState('');
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    const clean = postcode.trim().toUpperCase().replace(/\s/g, '');
    if (!clean) return;
    setLoading(true);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${clean}`);
      const data = await res.json();
      if (data.status === 200 && data.result) {
        const r = data.result;
        onFound({
          zip_postal:    postcode.trim().toUpperCase(),
          city:          r.admin_district || r.parish || '',
          state_province:r.admin_county   || r.european_electoral_region || '',
          country:       'United Kingdom',
        });
        toast.success(`Postcode ${clean} found: ${r.admin_district}`);
      } else {
        toast.error('Postcode not found');
      }
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Postcode lookup failed'); }
    setLoading(false);
  };

  return (
    <div className="flex gap-2">
      <Input value={postcode} onChange={e => setPostcode(e.target.value.toUpperCase())}
        placeholder="e.g. SW1A 1AA" className="h-9 flex-1 font-mono"
        onKeyDown={e => e.key === 'Enter' && lookup()} />
      <Button size="sm" variant="outline" onClick={lookup} disabled={loading || !postcode.trim()}>
        {loading ? '…' : 'Lookup'}
      </Button>
    </div>
  );
}
